/**
 * The rail fence cipher: write the message in a zigzag across a number of rails, then
 * read the rails one after another.
 *
 * Every character given is moved — the caller decides beforehand whether spaces and
 * punctuation are part of the message (see `applyTextOptions`). Characters are code
 * points, not UTF-16 units, so an emoji travels the fence as one character.
 *
 * The offset starts the zigzag part-way through its cycle: offset k is the same fence as
 * k blank characters written first and then left out.
 */

export const MIN_RAILS = 2;
export const MAX_RAILS = 10;

/** Positions in one down-and-up sweep: 2(rails − 1). */
export function railCycle(rails: number): number {
  return Math.max(1, 2 * (rails - 1));
}

/** The rail (0-based, top first) each of `length` characters sits on. */
export function railPattern(
  length: number,
  rails: number,
  offset = 0
): number[] {
  const cycle = railCycle(rails);
  const pattern: number[] = [];
  for (let index = 0; index < length; index += 1) {
    const position = (index + offset) % cycle;
    pattern.push(position < rails ? position : cycle - position);
  }
  return pattern;
}

/** Each rail's characters, top rail first. */
export function railRows(text: string, rails: number, offset = 0): string[] {
  const chars = [...text];
  const pattern = railPattern(chars.length, rails, offset);
  const rows: string[] = Array.from({ length: rails }, () => "");
  for (const [index, char] of chars.entries()) {
    rows[pattern[index]] += char;
  }
  return rows;
}

export function railFenceEncode(
  text: string,
  rails: number,
  offset = 0
): string {
  return railRows(text, rails, offset).join("");
}

export function railFenceDecode(
  text: string,
  rails: number,
  offset = 0
): string {
  const chars = [...text];
  const pattern = railPattern(chars.length, rails, offset);
  const counts: number[] = Array.from({ length: rails }, () => 0);
  for (const rail of pattern) {
    counts[rail] += 1;
  }
  // Cut the cipher text into rails of exactly those lengths…
  const rows: string[][] = [];
  let start = 0;
  for (const count of counts) {
    rows.push(chars.slice(start, start + count));
    start += count;
  }
  // …then walk the zigzag again, taking the next character from each rail it visits.
  const cursors: number[] = Array.from({ length: rails }, () => 0);
  let plain = "";
  for (const rail of pattern) {
    plain += rows[rail][cursors[rail]];
    cursors[rail] += 1;
  }
  return plain;
}

/**
 * The zigzag as a grid: `rails` rows by one column per character, with the character
 * in its rail's row and `null` everywhere else.
 */
export function railFenceGrid(
  text: string,
  rails: number,
  offset = 0
): (string | null)[][] {
  const chars = [...text];
  const pattern = railPattern(chars.length, rails, offset);
  const grid: (string | null)[][] = Array.from({ length: rails }, () =>
    Array.from({ length: chars.length }, () => null)
  );
  for (const [index, char] of chars.entries()) {
    grid[pattern[index]][index] = char;
  }
  return grid;
}
